import { Card, CardMedia, CardContent, Typography, Button, Chip, Box, Tooltip } from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import LoopIcon from '@mui/icons-material/Loop';
import { Link } from 'react-router-dom';
import { SITE_URL } from '../constants';

const slugify = (text) =>
  text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const ProductCard = ({ product, onAddToCart, onZoom }) => {
  const slug = slugify(product.nombre);
  return (
    <Card 
      // Microdatos para Agentes de IA
      itemScope itemType="https://schema.org/Product"
      elevation={0} 
      sx={{ 
        borderRadius: 2, // Un toque más moderno
        bgcolor: 'background.paper', 
        width: '100%', 
        maxWidth: 300,
        transition: 'all 0.3s ease',
        '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }
      }}>
      
      {/* Imagen con Microdatos */}
      <Box 
        sx={{ 
          position: 'relative', 
          paddingTop: '133.33%', 
          cursor: 'zoom-in',
          overflow: 'hidden',
          borderRadius: '8px 8px 0 0'
        }}
        onClick={() => onZoom(product.imagenUrl)}
      >
        <CardMedia
          component="img" 
          itemProp="image"
          image={product.imagenUrl} 
          alt={product.nombre}
          sx={{ 
            position: 'absolute', 
            top: 0, left: 0, width: '100%', height: '100%',
            objectFit: 'cover',
            transition: 'transform 0.5s ease',
            '&:hover': { transform: 'scale(1.1)' }
          }}
        />
        
        {/* Badge de Nivel de Confianza (GXO) */}
        {product.gx?.confidence_level === 'high' && (
          <Chip 
            label="Alta Confianza" 
            size="small" 
            color="success"
            sx={{ position: 'absolute', top: 10, right: 10, fontWeight: 'bold', fontSize: '10px' }}
          />
        )}
      </Box>

      <CardContent sx={{ p: 2 }}>
        {/* Categoría y Nombre */}
        <Typography itemProp="category" variant="caption" sx={{ color: '#999', fontWeight: 600, display: 'block', textTransform: 'uppercase' }}>
          {product.categoria}
        </Typography>
        
        <Typography 
          itemProp="name" variant="subtitle1" sx={{ fontWeight: 800, mt: 0.5, lineHeight: 1.2, height: '42px', overflow: 'hidden' }}
          onClick={() => window.location.href = `${SITE_URL}/producto/${slug}.html`}  
        >
          {product.nombre}
        </Typography>

        {/* Precio Estructurado para Agentes */}
        <Box itemProp="offers" itemScope itemType="https://schema.org/Offer" sx={{ mt: 1 }}>
          <meta itemProp="priceCurrency" content="BOB" />
          <Typography variant="h6" sx={{ fontWeight: 900, color: '#000' }}>
            BOB <span itemProp="price">{product.precio.toFixed(2)}</span>
          </Typography>
        </Box>

        {/* Beneficio Principal (Agent-Friendly info) */}
        {product.gx?.main_benefit && (
          <Typography variant="body2" sx={{ mt: 1, color: '#444', fontStyle: 'italic', fontSize: '0.85rem' }}>
            ✨ {product.gx.main_benefit}
          </Typography>
        )}

        {/* Tags de Decisión (Best For) */}
        <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5, minHeight: '32px' }}>
          {product.gx?.best_for?.map((tag, index) => (
            <Chip 
              key={index} 
              label={tag} 
              size="small" 
              sx={{ bgcolor: '#f0f4f8', color: '#475569', fontSize: '10px', borderRadius: '4px' }} 
            />
          ))}
        </Box>

        {/* Indicadores de Confianza (Trust) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, pt: 1, borderTop: '1px solid #f0f0f0' }}>
          {product.trust?.dermatologically_tested && (
            <Tooltip title="Dermatológicamente Testeado">
              <VerifiedUserIcon sx={{ fontSize: 16, color: '#4CAF50' }} />
            </Tooltip>
          )}
          {product.trust?.refund_days > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', color: '#666' }}>
              <LoopIcon sx={{ fontSize: 14, mr: 0.5 }} />
              <Typography variant="caption" sx={{ fontWeight: 600 }}>{product.trust.refund_days}d garantía</Typography>
            </Box>
          )}
        </Box>

        <Button 
          fullWidth variant="contained" disableElevation
          onClick={() => onAddToCart(product)}
          sx={{ 
            bgcolor: '#000', color: '#fff', mt: 2, borderRadius: '6px',
            textTransform: 'none', fontWeight: 800, py: 1,
            '&:hover': { bgcolor: '#333' }
          }}
        >
          Agregar al Carrito
        </Button>
      </CardContent>
    </Card>
  );
};

export default ProductCard;